import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Flex,
  FormLabel,
  Icon,
  Text,
} from "@chakra-ui/react";
import {
  AsyncSelect,
  type ChakraStylesConfig,
  type GroupBase,
  type OptionProps,
  chakraComponents,
} from "chakra-react-select";
import { useEffect, useRef, useState } from "react";

import {
  DonorSearchMode,
  PortfolioSyncMode,
  type RuleQuery,
  useTargetSelectorDefaultsQuery,
  useTargetSelectorHouseholdsDefaultsQuery,
  useTargetSelectorHouseholdsSearchLazyQuery,
  useTargetSelectorSearchLazyQuery,
} from "@/generated/graphql";

import { icons } from "@/app/theme";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";

export type Target = {
  __typename?:
    | NonNullable<RuleQuery["rule"]>["plans"][0]["__typename"]
    | NonNullable<RuleQuery["rule"]>["donors"][0]["__typename"]
    | "CrmDonorTypeaheadType"
    | "GQLDonorList";
  value: string;
  label: string;
  donorsCount: number;
  disabled?: boolean;
  isHousehold?: boolean;
};

export type TargetInput = Target[] | Target | undefined;

export const flattenTargetInput = (input: TargetInput): Target[] => {
  if (!input) return [];
  if (Array.isArray(input)) return input;
  return [input];
};

const transformToTarget = (donor: {
  __typename?: "CrmDonorType" | "CrmDonorTypeaheadType";
  id: string;
  preferredName: string;
  name: string;
  companyName: string;
}): Target => {
  return {
    __typename: donor.__typename,
    value: donor.id,
    label: donor.name || donor.companyName,
    donorsCount: 1,
  };
};

// A household is picked through its head donor, so the action targets one person.
export const transformHouseholdToTarget = (household: {
  name: string;
  headDonorId: string;
}): Target => ({
  __typename: "CrmDonorType",
  value: household.headDonorId,
  label: household.name,
  donorsCount: 1,
  isHousehold: true,
});

export const mergeHouseholdTargets = (households: Target[], donors: Target[]): Target[] => {
  const headIds = new Set(households.map((household) => household.value));
  return [...households, ...donors.filter((donor) => !headIds.has(donor.value))];
};

export const TargetOption = (props: OptionProps<Target, boolean>) => {
  const { children, data, innerRef, innerProps, isSelected } = props;
  const color = isSelected ? "white" : "blue.500";

  return (
    <chakraComponents.Option {...props}>
      <Flex align="center" ref={innerRef} {...innerProps}>
        {data.isHousehold ? (
          <Icon as={icons.household} color={color} mr={2} />
        ) : data.__typename === "GQLDonorList" || data.__typename !== "CrmDonorType" ? (
          <Icon
            as={data.__typename === "GQLDonorList" ? icons.lists : icons.segments}
            color={color}
            mr={2}
          />
        ) : null}
        <Text>{children}</Text>
      </Flex>
    </chakraComponents.Option>
  );
};

const PLACEHOLDER = `Search constituents`;

export function AutoSelectField({
  onChange,
  value,
  placeholder = PLACEHOLDER,
  excludeIndividuals = false,
  excludePlans = false,
  excludeDonorLists = false,
  excludeHouseholds = false,
  singleIndividualOnly = false,
  isDisabled,
  includeAllDonors = false,
}: {
  onChange: (value: TargetInput) => void;
  value: TargetInput;
  placeholder?: string;
  excludeIndividuals?: boolean;
  excludePlans?: boolean;
  excludeDonorLists?: boolean;
  excludeHouseholds?: boolean;
  singleIndividualOnly?: boolean;
  isDisabled?: boolean;
  includeAllDonors?: boolean;
}) {
  const chakraStyles: ChakraStylesConfig<Target, boolean, GroupBase<Target>> = {
    valueContainer: (provided) => ({
      ...provided,
      gridAutoColumns: "1fr",
      flexWrap: "wrap",
      maxHeight: "12rem",
      overflowY: "auto",
    }),
  };

  const { data, loading: donorsLoading } = useTargetSelectorDefaultsQuery();
  const { data: householdDefaults, loading: householdsLoading } =
    useTargetSelectorHouseholdsDefaultsQuery({ skip: excludeIndividuals || excludeHouseholds });
  // Showing donors before households arrive would list the head donor, then swap it out.
  const loading = donorsLoading || householdsLoading;
  const [searchDonorsAndPortfoliosQuery] = useTargetSelectorSearchLazyQuery();
  const [searchHouseholdsQuery] = useTargetSelectorHouseholdsSearchLazyQuery();
  const searchMode = includeAllDonors
    ? DonorSearchMode.AnyInOrg
    : DonorSearchMode.AssignedToUserActively;

  /**
   *
   * Shared debounced search with promise resolution for loadOptions
   * We are forced to work within the AsyncSelect loadOptions API which requires
   * us to return a Promise immediately, but we also want to debounce the actual
   * search calls to avoid flooding the server with requests on every keystroke.
   *
   * The approach here is to use a debounced function to perform the search,
   * and maintain a single shared resolver for the most recent loadOptions call.
   *
   * When the debounced search completes, it resolves that promise with the results.
   *
   */
  const pendingResolverRef = useRef<((value: Target[]) => void) | null>(null);
  const searchCountRef = useRef(0);

  const debouncedSearch = useDebouncedCallback(
    async (value: string, searchId: number) => {
      let results: Target[] = [];
      try {
        const variables = { query: value, mode: searchMode };
        // A failed household search must not hide the donor results.
        const [{ data }, households] = await Promise.all([
          searchDonorsAndPortfoliosQuery({ variables }),
          excludeIndividuals || excludeHouseholds
            ? undefined
            : searchHouseholdsQuery({ variables }).catch(() => undefined),
        ]);
        const householdTargets = (households?.data?.crmHouseholdsTypeahead ?? []).map(
          transformHouseholdToTarget,
        );
        const donors = excludeIndividuals
          ? []
          : (data?.crmDonorsTypeahead ?? []).map(transformToTarget);

        results = [
          ...(excludePlans ? [] : data?.portfolios || []),
          ...(excludeDonorLists ? [] : data?.donorLists || []),
          ...mergeHouseholdTargets(householdTargets, donors),
        ];
      } catch {
        toast.error("Search failed. Please try again.", {
          position: "bottom-right",
          autoClose: 5_000,
          closeOnClick: true,
          draggable: false,
        });
      }
      // A search older than the newest keystroke must not answer that keystroke's promise.
      if (searchId !== searchCountRef.current) return;
      if (pendingResolverRef.current) {
        pendingResolverRef.current(results);
        pendingResolverRef.current = null;
      }
    },
    400,
    { leading: false },
  );

  // Must return a Promise immediately
  function loadOptions(inputValue: string): Promise<Target[]> {
    return new Promise((resolve) => {
      // Resolve any previously pending promise to avoid leaving it hanging
      if (pendingResolverRef.current) {
        pendingResolverRef.current([]);
      }

      pendingResolverRef.current = resolve;
      debouncedSearch(inputValue, ++searchCountRef.current);
    });
  }

  const defaultDonors = mergeHouseholdTargets(
    (householdDefaults?.crmHouseholdsTypeahead ?? []).map(transformHouseholdToTarget),
    (data?.crmDonorsTypeahead ?? []).map(transformToTarget),
  );

  // Filter out any automatic segments. We don't want users to send bulk outreach to them.
  // They should move specific donors to other manual segments first.
  const defaultPortfolios =
    data?.portfolios.filter((plan) => plan.syncMode === PortfolioSyncMode.Manual) ?? [];
  const defaultDonorLists = data?.donorLists ?? [];
  const defaultOptions = loading
    ? [
        {
          label: "Loading...",
          value: "loading",
          isDisabled: true,
          donorsCount: 0,
        },
      ]
    : [
        ...(excludePlans ? [] : defaultPortfolios.filter((portfolio) => portfolio.donorsCount > 0)),
        ...(excludeDonorLists ? [] : defaultDonorLists.filter((list) => list.donorsCount > 0)),
        ...(excludeIndividuals ? [] : defaultDonors),
      ].filter((option) => option.label !== PLACEHOLDER);

  const customComponents = { Option: TargetOption };

  return (
    <AsyncSelect
      chakraStyles={chakraStyles}
      placeholder={
        excludePlans ? "Select donor" : excludeIndividuals ? "Target group(s)" : placeholder
      }
      isMulti={!singleIndividualOnly}
      loadOptions={loadOptions}
      defaultOptions={defaultOptions}
      onChange={(value) => onChange(value as Target[])}
      openMenuOnFocus={false}
      openMenuOnClick={true}
      value={value}
      autoFocus
      isDisabled={isDisabled}
      components={customComponents}
    />
  );
}

export function IncludeExclude({
  onChange,
  include,
  exclude,
  startExpanded,
  plansOnly,
  noExclude,
  isDisabled,
  hideLabel,
  individualsOnly,
  singleIndividualOnly,
  excludeDonorLists = false,
  includeAllDonors = false,
}: {
  onChange: (args: { include: TargetInput; exclude: TargetInput }) => void;
  include: TargetInput;
  exclude?: TargetInput;
  startExpanded: boolean;
  plansOnly?: boolean;
  individualsOnly?: boolean;
  noExclude?: boolean;
  isDisabled?: boolean;
  hideLabel?: boolean;
  singleIndividualOnly?: boolean;
  excludeDonorLists?: boolean;
  includeAllDonors?: boolean;
}) {
  const [excludedToggleOpen, setExcludedToggleOpen] = useState(startExpanded);
  const [includeItems, setIncludeItems] = useState<TargetInput>(include);
  const [excludeItems, setExcludeItems] = useState<TargetInput>(exclude);
  useEffect(() => {
    setIncludeItems(include);
    setExcludeItems(exclude);
  }, [include, exclude]);
  useEffect(() => {
    onChange({ include: includeItems, exclude: excludeItems });
  }, [includeItems, excludeItems]);
  useEffect(() => {
    setExcludedToggleOpen(startExpanded);
  }, [startExpanded]);
  return (
    <Flex flexDirection="column" flex={1}>
      {!hideLabel && (
        <FormLabel fontWeight="bold" mb={1}>
          Who?
        </FormLabel>
      )}
      <AutoSelectField
        value={includeItems}
        onChange={setIncludeItems}
        excludeIndividuals={plansOnly}
        isDisabled={isDisabled}
        excludePlans={individualsOnly}
        singleIndividualOnly={singleIndividualOnly}
        excludeDonorLists={excludeDonorLists}
        includeAllDonors={includeAllDonors}
      />
      {!noExclude && (
        <Accordion
          allowToggle
          p={0}
          border="1px solid white"
          overflow="visible"
          index={excludedToggleOpen ? 0 : []}
          onChange={() => setExcludedToggleOpen(!excludedToggleOpen)}
        >
          <AccordionItem p={0}>
            <AccordionButton _hover={{ bg: "transparent" }} p={0}>
              <Text fontSize="xs" color="gray.500" pt={1}>
                Exclude
              </Text>
              <AccordionIcon color="gray.500" />
            </AccordionButton>
            <AccordionPanel p={0} pt={1}>
              <AutoSelectField
                placeholder="Exclude…"
                value={exclude}
                onChange={setExcludeItems}
                isDisabled={isDisabled}
                excludeDonorLists={true}
                excludeHouseholds={true}
              />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}
    </Flex>
  );
}
